import React, { useState, useEffect } from 'react';
import {
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Copy,
  RefreshCw,
  Loader2,
  Check,
  Github,
  Wallet,
  ShieldCheck,
  Droplets,
  Clock,
  ArrowRight,
} from 'lucide-react';
import ReactGA from 'react-ga4';
import FaucetStatusBadge from './FaucetStatusBadge';

const OPNFaucet = () => {
  const [address, setAddress] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');
  const [cooldownTime, setCooldownTime] = useState(null);
  const [copied, setCopied] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [canRetry, setCanRetry] = useState(false);
  const [githubLink, setGithubLink] = useState(null);
  const [githubEligibility, setGithubEligibility] = useState(null);
  const [githubMessage, setGithubMessage] = useState('');
  const [githubError, setGithubError] = useState('');
  const [isLinkingGithub, setIsLinkingGithub] = useState(false);
  
  // Text CAPTCHA state
  const [textCaptcha, setTextCaptcha] = useState({ id: '', svg: '' });
  const [textCaptchaAnswer, setTextCaptchaAnswer] = useState('');
  const [isLoadingTextCaptcha, setIsLoadingTextCaptcha] = useState(false);
  
  // Validation state
  const [validationError, setValidationError] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  // Backend API URL - update this to match your backend
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Network configuration for MetaMask
  const networkConfig = {
    chainId: '0x3d8', 
    chainName: 'OPN Testnet',
    rpcUrls: ['https://testnet-rpc.iopn.tech'],
    blockExplorerUrls: ['https://testnet.iopn.tech'],
    nativeCurrency: {
      name: 'IOPN',
      symbol: 'IOPN',
      decimals: 18
    }
  };

  const faucetAddress = '0x05b2fe1d1Ae9B69dAeb16090Af4841E7DA09B111';

  // Function to fetch new text CAPTCHA
  const fetchTextCaptcha = async () => {
    setIsLoadingTextCaptcha(true);
    try {
      const response = await fetch(`${API_URL}/api/faucet/captcha`);
      const data = await response.json();
      setTextCaptcha({
        id: data.captchaId,
        svg: data.captcha
      });
      setTextCaptchaAnswer(''); // Clear previous answer
    } catch (error) {
      console.error('Failed to load text CAPTCHA:', error);
    } finally {
      setIsLoadingTextCaptcha(false);
    }
  };

  // Load text CAPTCHA on component mount
  useEffect(() => {
    fetchTextCaptcha();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const githubState = params.get('github');
    const wallet = params.get('wallet');
    const login = params.get('login');
    const error = params.get('error');

    if (!githubState) {
      return;
    }

    if (wallet && validateAddress(wallet)) {
      setAddress(wallet);
      fetchGithubStatus(wallet);
    }

    if (githubState === 'linked') {
      setGithubError('');
      setGithubMessage(login ? `GitHub linked: @${login}` : 'GitHub linked successfully.');
      if (wallet && validateAddress(wallet)) {
        fetchGithubEligibility(wallet);
      }
    } else {
      setGithubLink(null);
      setGithubMessage('');
      setGithubError(error || 'GitHub linking failed.');
    }

    setIsLinkingGithub(false);
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  // Check faucet claim cooldown status for the current IP
  const checkFaucetStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/faucet/info`);
      const data = await response.json();

      if (!data.canClaim && data.hoursUntilNextClaim > 0) {
        setCooldownTime(data.hoursUntilNextClaim);
      } else {
        setCooldownTime(null);
      }
    } catch (error) {
      console.error('Error checking faucet status:', error);
    }
  };

  const validateAddress = (addr) => {
    return /^0x[a-fA-F0-9]{40}$/.test(addr);
  };

  const githubRejectionReasons = (githubEligibility?.reasons || []).filter((reason) => reason !== 'cached');
  const githubRejectionMessage =
    githubRejectionReasons.length > 0
      ? githubRejectionReasons.join('. ')
      : 'This GitHub account is not eligible for the faucet.';

  const fetchGithubEligibility = async (walletAddress) => {
    if (!validateAddress(walletAddress)) {
      setGithubEligibility(null);
      return null;
    }

    try {
      const response = await fetch(
        `${API_URL}/auth/github/eligibility?wallet=${encodeURIComponent(walletAddress)}`,
      );
      const data = await response.json();

      if (!response.ok) {
        setGithubEligibility(null);
        return null;
      }

      setGithubEligibility(data);
      return data;
    } catch (error) {
      console.error('Error checking GitHub eligibility:', error);
      return null;
    }
  };

  const fetchGithubStatus = async (walletAddress) => {
    if (!validateAddress(walletAddress)) {
      setGithubLink(null);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/github/status?wallet=${encodeURIComponent(walletAddress)}`);
      const data = await response.json();

      if (!response.ok) {
        setGithubLink(null);
        return;
      }

      setGithubLink(data.linked ? data : null);
      if (data.linked) {
        await fetchGithubEligibility(walletAddress);
      } else {
        setGithubEligibility(null);
      }
    } catch (error) {
      console.error('Error checking GitHub link status:', error);
      setGithubLink(null);
    }
  };

  const connectGithub = () => {
    if (!validateAddress(address)) {
      setGithubError('Enter a valid wallet address before connecting GitHub.');
      setGithubMessage('');
      return;
    }

    setIsLinkingGithub(true);
    setGithubError('');
    setGithubMessage('Redirecting to GitHub...');
    window.location.assign(`${API_URL}/auth/github/start?wallet=${encodeURIComponent(address)}`);
  };

  const validateClaimPreCheck = async () => {
    if (!validateAddress(address)) {
      setValidationError('Enter a valid wallet address');
      return false;
    }
    
    if (!githubLink) {
      setValidationError('Connect GitHub first');
      return false;
    }

    setIsValidating(true);
    try {
      const response = await fetch(`${API_URL}/api/faucet/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          walletAddress: address,
          githubId: githubLink.githubId,
          ipAddress: 'auto' // Backend will determine from request headers
        })
      });
      
      const data = await response.json();
      
      if (!data.allowed) {
        setValidationError(data.reason || 'Validation failed');
        return false;
      }
      
      setValidationError('');
      return true;
    } catch (error) {
      console.error('Validation error:', error);
      setValidationError('Validation error. Try again.');
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const addToMetaMask = async () => {
    try {
      if (typeof window.ethereum === 'undefined') {
        alert('Please install MetaMask to use this feature');
        return;
      }

      await window.ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [networkConfig]
      });
    } catch (error) {
      console.error('Error adding network:', error);
    }
  };

  const viewExplorer = () => {
    window.open(networkConfig.blockExplorerUrls[0], '_blank');
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(faucetAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const claimTokens = async () => {
    // Validate claim before proceeding
    const isValid = await validateClaimPreCheck();
    if (!isValid) return;

    if (!textCaptchaAnswer) {
      setStatus('error');
      setMessage('Please enter the CAPTCHA');
      setCanRetry(false);
      return;
    }

    if (!githubLink || githubEligibility?.eligible !== true) {
      setStatus('error');
      setMessage(
        (githubEligibility?.eligible === false ? githubRejectionMessage : null) ??
          (githubLink
            ? 'GitHub eligibility check in progress. Please wait a moment and try again.'
            : 'Connect and verify your GitHub account before claiming.'),
      );
      setCanRetry(false);
      return;
    }

    setStatus('loading');
    setMessage('');
    setTxHash('');
    setCanRetry(false);

    try {
      const response = await fetch(`${API_URL}/api/faucet/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          address,
          captchaId: textCaptcha.id,
          captchaAnswer: textCaptchaAnswer
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setStatus('error');
        
        // If CAPTCHA was wrong, refresh it
        if (data.error === 'Incorrect CAPTCHA answer' || data.error === 'CAPTCHA expired or invalid') {
          fetchTextCaptcha();
        }
        
        // Check if it's a transaction failure (500 error)
        if (response.status === 500 && data.error === 'Transaction failed') {
          setMessage('Transaction failed due to network congestion. Please try again in a few moments.');
          setCanRetry(true);
          
          // Auto clear error after 10 seconds
          setTimeout(() => {
            if (status === 'error' && canRetry) {
              setStatus('idle');
              setMessage('');
              setCanRetry(false);
            }
          }, 10000);
        } else {
          const reasons = (data.reasons || []).filter((reason) => reason !== 'cached');
          setMessage(reasons.length > 0 ? reasons.join('. ') : data.error || 'Failed to claim tokens');
          setCanRetry(false);
          
          if (response.status === 429) {
            const statusResponse = await fetch(`${API_URL}/api/faucet/status`);
            const statusData = await statusResponse.json();
            setCooldownTime(statusData.hoursUntilNextClaim);
          }
        }
        
        return;
      }

      // Direct success
      setStatus('success');
      setMessage(`Successfully sent ${data.amount} IOPN to ${data.recipient.slice(0, 6)}...${data.recipient.slice(-4)}`);
      setTxHash(data.txHash);
      
      ReactGA.event({
        category: 'Faucet',
        action: 'Claim',
        label: 'IOPN',
        value: 2
      });

      fetchTextCaptcha(); // Get new CAPTCHA for next claim
      setTimeout(() => {
        checkFaucetStatus();
      }, 2000);
      
      setTimeout(() => {
        setAddress('');
        setStatus('idle');
        setMessage('');
        setTxHash('');
      }, 10000);
      
    } catch (error) {
      console.error('Claim error:', error);
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
      setCanRetry(true);
      
      // Auto clear error after 10 seconds
      setTimeout(() => {
        if (status === 'error' && canRetry) {
          setStatus('idle');
          setMessage('');
          setCanRetry(false);
        }
      }, 10000);
    }
  };

  useEffect(() => {
    checkFaucetStatus();
    // Check status every 30 seconds
    const interval = setInterval(checkFaucetStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!validateAddress(address)) {
      setGithubLink(null);
      setGithubEligibility(null);
      setGithubError('');
      return;
    }

    fetchGithubStatus(address);
  }, [address]);

  useEffect(() => {
    if (!githubLink || githubEligibility?.eligible !== null) {
      return undefined;
    }

    let attempts = 0;
    const interval = setInterval(async () => {
      attempts += 1;
      const data = await fetchGithubEligibility(address);
      if (data?.eligible !== null || attempts >= 30) {
        clearInterval(interval);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [githubLink, githubEligibility?.eligible, address]);

  // ---- Derived UI state for the guided stepper ----
  const addressIsValid = validateAddress(address);
  const githubVerified = !!githubLink && githubEligibility?.eligible === true;
  const captchaReady = !!textCaptchaAnswer;
  const isBusy = status === 'loading' || isValidating;
  const claimDisabled =
    isBusy || !!cooldownTime || !captchaReady || !githubLink || githubEligibility?.eligible !== true;

  // The step the user is currently expected to act on (drives the "active" ring)
  const activeStep = !addressIsValid ? 1 : !githubVerified ? 2 : 3;

  const stepState = (n, done) => (done ? 'done' : activeStep === n ? 'active' : 'todo');

  const badgeClasses = (state) =>
    state === 'done'
      ? 'bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white border-transparent shadow-lg shadow-purple-500/30'
      : state === 'active'
      ? 'bg-purple-500/15 text-purple-200 border-purple-400/60 ring-4 ring-purple-500/10'
      : 'bg-white/5 text-gray-500 border-white/10';

  return (
    <div className="min-h-screen bg-[#07060d] text-white relative overflow-hidden">
      {/* Animated ambient background */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-32 h-[28rem] w-[28rem] rounded-full bg-purple-600/30 blur-3xl animate-blob" />
        <div className="absolute top-24 -right-40 h-[26rem] w-[26rem] rounded-full bg-fuchsia-600/20 blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute -bottom-48 left-1/3 h-[30rem] w-[30rem] rounded-full bg-indigo-700/20 blur-3xl animate-blob animation-delay-4000" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(147,51,234,0.22),transparent_55%)]" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full min-h-screen flex flex-col">
        {/* Top nav */}
        <header className="w-full border-b border-white/5 backdrop-blur-sm">
          <div className="container mx-auto px-4 max-w-5xl flex items-center justify-between py-4">
            <div className="flex items-center gap-2.5">
              <img src="/logo.png" alt="IOPn Logo" className="h-8 w-auto" />
              <span className="font-semibold tracking-tight text-white/90">OPN Faucet</span>
            </div>
            <nav className="hidden sm:flex items-center gap-7 text-sm">
              <a href="https://iopn.io" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Website</a>
              <a href="https://iopn.gitbook.io/iopn/developer-docs" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Docs</a>
              <a href="https://testnet.iopn.tech" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Explorer</a>
            </nav>
          </div>
        </header>

        {/* Hero */}
        <section className="pt-14 pb-10">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <div className="flex justify-center mb-6">
              <div className="animate-float relative">
                <div className="absolute inset-0 -z-10 blur-2xl bg-purple-500/40 rounded-full" />
                <img src="/logo.png" alt="IOPn Logo" className="h-16 w-auto" />
              </div>
            </div>

            <div className="mb-5 flex justify-center">
              <FaucetStatusBadge apiUrl={API_URL} />
            </div>

            <h1 className="text-3xl sm:text-6xl font-extrabold tracking-tight mb-4 leading-[1.05]">
              <span className="bg-gradient-to-r from-white via-purple-200 to-fuchsia-300 bg-clip-text text-transparent text-shimmer">
                Fuel your build on OPN
              </span>
            </h1>
            <p className="text-gray-400 text-base sm:text-lg max-w-xl mx-auto">
              Claim <span className="text-white font-semibold">2 IOPN</span> of free OPN Testnet tokens for every
              GitHub-verified wallet, once every 24 hours.
            </p>
          </div>
        </section>

        {/* Claim card */}
        <div className="flex-1 container mx-auto px-4 max-w-2xl w-full">
          <div className="relative rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 sm:p-8 shadow-2xl shadow-purple-950/40">
            {/* subtle top highlight */}
            <div className="pointer-events-none absolute inset-x-10 -top-px h-px bg-gradient-to-r from-transparent via-purple-400/50 to-transparent" />

            {/* ============ STEP 1 — Wallet ============ */}
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-all ${badgeClasses(stepState(1, addressIsValid))}`}>
                  {addressIsValid ? <Check className="w-4 h-4" /> : '1'}
                </div>
                <div className="w-px flex-1 my-2 bg-gradient-to-b from-white/15 to-white/0" />
              </div>
              <div className="flex-1 pb-7">
                <label htmlFor="wallet-address" className="block text-sm font-semibold text-white mb-1">
                  Enter your wallet
                </label>
                <p className="text-xs text-gray-500 mb-3">The address that will receive the 2 IOPN test tokens.</p>
                <div className="relative">
                  <Wallet className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    id="wallet-address"
                    name="wallet-address"
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="0x0000...0000"
                    className={`w-full pl-10 pr-10 py-3 bg-black/40 border rounded-xl text-white placeholder-gray-600 focus:outline-none transition-all font-mono text-sm ${
                      address && !addressIsValid
                        ? 'border-red-500/50 focus:border-red-500/70'
                        : addressIsValid
                        ? 'border-green-500/50 focus:border-green-500/70'
                        : 'border-white/10 focus:border-purple-500/60'
                    }`}
                    disabled={status === 'loading'}
                    spellCheck="false"
                    autoComplete="off"
                  />
                  {addressIsValid && (
                    <CheckCircle className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                  )}
                </div>
                {address && !addressIsValid && (
                  <p className="mt-2 text-xs text-red-400 flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5" /> That doesn’t look like a valid 0x address.
                  </p>
                )}
              </div>
            </div>

            {/* ============ STEP 2 — GitHub ============ */}
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-all ${badgeClasses(stepState(2, githubVerified))}`}>
                  {githubVerified ? <Check className="w-4 h-4" /> : '2'}
                </div>
                <div className="w-px flex-1 my-2 bg-gradient-to-b from-white/15 to-white/0" />
              </div>
              <div className={`flex-1 pb-7 transition-opacity ${addressIsValid ? '' : 'opacity-50'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-white mb-1">Verify with GitHub</p>
                    <p className="text-xs text-gray-500">
                      Proves you’re a real developer. We request <span className="text-gray-400">read:user</span> only — no token is stored.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={connectGithub}
                    disabled={status === 'loading' || isLinkingGithub || !addressIsValid}
                    className={`shrink-0 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all ${
                      status === 'loading' || isLinkingGithub || !addressIsValid
                        ? 'bg-white/5 text-gray-500 cursor-not-allowed'
                        : 'bg-white text-black hover:bg-gray-200'
                    }`}
                  >
                    <Github className="w-4 h-4" />
                    {isLinkingGithub ? 'Opening…' : githubLink ? 'Reconnect' : 'Connect'}
                  </button>
                </div>

                {githubLink && (
                  <div className="mt-3 flex items-center gap-2.5 rounded-xl border border-white/10 bg-black/30 p-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-500/15 text-purple-300">
                      <Github className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium truncate">@{githubLink.login}</p>
                      <p className="text-xs text-gray-500">GitHub ID {githubLink.githubId}</p>
                    </div>
                  </div>
                )}

                {githubLink && githubEligibility?.eligible === null && (
                  <div className="mt-3 flex items-center gap-2 rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm text-yellow-300">
                    <Loader2 className="w-4 h-4 animate-spin" /> Checking eligibility…
                  </div>
                )}

                {githubVerified && (
                  <div className="mt-3 flex items-center gap-2 rounded-xl border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-300">
                    <ShieldCheck className="w-4 h-4" /> Verified — you’re eligible to claim.
                  </div>
                )}

                {githubLink && githubEligibility?.eligible === false && (
                  <div className="mt-3 flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <p>{githubRejectionMessage}</p>
                  </div>
                )}

                {githubMessage && !githubLink && (
                  <div className="mt-3 rounded-xl border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-300">
                    {githubMessage}
                  </div>
                )}

                {githubError && (
                  <div className="mt-3 flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <p>{githubError}</p>
                  </div>
                )}
              </div>
            </div>

            {/* ============ STEP 3 — CAPTCHA + claim ============ */}
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-all ${badgeClasses(stepState(3, status === 'success'))}`}>
                  {status === 'success' ? <Check className="w-4 h-4" /> : '3'}
                </div>
              </div>
              <div className={`flex-1 transition-opacity ${githubVerified ? '' : 'opacity-50'}`}>
                <p className="text-sm font-semibold text-white mb-1">Solve the security check & claim</p>
                <p className="text-xs text-gray-500 mb-3">Type the characters below to prove you’re human.</p>

                <div className="bg-black/40 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex-1 flex justify-center" style={{ minHeight: '92px' }}>
                      {isLoadingTextCaptcha ? (
                        <div className="flex items-center justify-center w-full">
                          <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
                        </div>
                      ) : (
                        <div
                          dangerouslySetInnerHTML={{ __html: textCaptcha.svg }}
                          className="svg-captcha-container flex items-center justify-center w-full rounded-lg"
                          style={{
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '12px',
                            padding: '14px',
                            maxWidth: '340px',
                            width: '100%',
                            filter: 'brightness(1.6) contrast(1.2)',
                          }}
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={fetchTextCaptcha}
                      className="p-2.5 hover:bg-white/10 rounded-lg transition-colors border border-white/10"
                      disabled={isLoadingTextCaptcha}
                      title="Refresh CAPTCHA"
                    >
                      <RefreshCw className={`w-4 h-4 text-gray-400 ${isLoadingTextCaptcha ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={textCaptchaAnswer}
                    onChange={(e) => setTextCaptchaAnswer(e.target.value)}
                    placeholder="Enter the characters"
                    className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-purple-500/60 transition-all text-center tracking-[0.3em] font-medium"
                    disabled={status === 'loading'}
                    maxLength="10"
                  />
                </div>

                {/* Validation error */}
                {validationError && (
                  <div className="mt-4 flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <p className="text-sm">{validationError}</p>
                  </div>
                )}

                {/* Claim button */}
                <button
                  onClick={claimTokens}
                  disabled={claimDisabled}
                  className={`group mt-4 w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 ${
                    claimDisabled
                      ? 'bg-white/5 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 shadow-lg shadow-purple-600/30 hover:shadow-purple-500/40'
                  }`}
                >
                  {isBusy ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {isValidating ? 'Validating…' : 'Processing…'}
                    </>
                  ) : (
                    <>
                      Request 2 IOPN
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>

                {/* Cooldown / status messages */}
                {(cooldownTime && status === 'idle') || message ? (
                  <div className="mt-4">
                    {cooldownTime && status === 'idle' && (
                      <div className="flex items-center gap-2 text-yellow-300 bg-yellow-500/10 border border-yellow-500/30 p-3 rounded-xl">
                        <Clock className="w-4 h-4" />
                        <p className="text-sm">You can claim again in {cooldownTime} hours.</p>
                      </div>
                    )}

                    {message && (
                      <div
                        className={`flex items-start gap-2 p-3 rounded-xl ${
                          status === 'success'
                            ? 'bg-green-500/10 border border-green-500/30 text-green-300'
                            : 'bg-red-500/10 border border-red-500/30 text-red-300'
                        }`}
                      >
                        {status === 'success' ? (
                          <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                        ) : (
                          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                        )}
                        <div className="w-full">
                          <p className="text-sm">{message}</p>
                          {txHash && (
                            <a
                              href={`${networkConfig.blockExplorerUrls[0]}/tx/${txHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs underline hover:no-underline mt-1 inline-flex items-center gap-1 text-purple-300"
                            >
                              View transaction <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                          {canRetry && status === 'error' && (
                            <button
                              onClick={() => {
                                setStatus('idle');
                                setMessage('');
                                setCanRetry(false);
                              }}
                              className="block text-xs font-medium bg-purple-600/20 hover:bg-purple-600/30 px-3 py-1 rounded-lg mt-2 transition-colors"
                            >
                              Try again
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Testnet note */}
          <p className="mt-4 text-center text-xs text-gray-600">
            This is a testnet faucet — IOPN test tokens hold no real value.
          </p>

          {/* Secondary actions */}
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              onClick={addToMetaMask}
              className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white/[0.03] border border-white/10 text-white hover:bg-white/[0.07] transition-all"
            >
              <img src="https://images.ctfassets.net/clixtyxoaeas/4rnpEzy1ATWRKVBOLxZ1Fm/a74dc1eed36d23d7ea6030383a4d5163/MetaMask-icon-fox.svg" alt="MetaMask" className="w-5 h-5" />
              <span className="text-sm font-medium">Add to MetaMask</span>
            </button>

            <button
              onClick={viewExplorer}
              className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white/[0.03] border border-white/10 text-white hover:bg-white/[0.07] transition-all"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="text-sm font-medium">Block Explorer</span>
            </button>
          </div>

          {/* Return address */}
          <div className="mt-4 mb-12 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-gray-400 text-sm mb-3 flex items-center gap-2">
              <Droplets className="w-4 h-4 text-purple-400" />
              Done testing? Return unused tokens to the faucet:
            </p>
            <div className="flex items-center gap-2 bg-black/40 border border-white/10 p-3 rounded-xl">
              <code className="flex-1 text-purple-300 text-xs font-mono break-all">{faucetAddress}</code>
              <button
                onClick={copyAddress}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors shrink-0"
                title="Copy address"
              >
                {copied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-auto">
          <div className="max-w-5xl mx-auto px-4">
            <div className="border-t border-white/5 py-8">
              <div className="flex flex-col items-center gap-5">
                <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm">
                  <a href="https://iopn.io" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Website</a>
                  <a href="https://iopn.gitbook.io/iopn/developer-docs" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Documentation</a>
                  <a href="https://testnet.iopn.tech" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Explorer</a>
                  <a href="https://x.com/iopn_io" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">X (Twitter)</a>
                  <a href="https://discord.gg/iopn" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Discord</a>
                </div>

                <p className="text-gray-600 text-xs text-center max-w-xl">
                  AI agents, bots, CI jobs, and automated tests should use the programmatic faucet API, not this web faucet.
                </p>

                <div className="text-center">
                  <p className="text-gray-500 text-xs">© {new Date().getFullYear()} IOPn • OPN Testnet Faucet v1.3</p>
                  <p className="text-gray-600 text-xs mt-1.5">Built with 💜 by the IOPn Team</p>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default OPNFaucet;
